import { z } from 'zod'

// Schema pour l'adresse
const addressSchema = z
  .object({
    line1: z.string().optional(),
    postalCode: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
  })
  .optional()

// Schema pour l'hébergeur
const hostingProviderSchema = z
  .object({
    name: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().email('Email invalide').optional().or(z.literal('')),
    address: z.string().optional(),
  })
  .optional()

// Schema pour les logiciels
const softwareItemSchema = z.object({
  name: z.string().min(1, 'Nom requis'),
  version: z.string().optional(),
  license: z.string().optional(),
  url: z.string().url('URL invalide').optional().or(z.literal('')),
})

// Schema pour l'échéancier de paiement
const paymentScheduleItemSchema = z.object({
  label: z.string().min(1, 'Libellé requis'),
  amount: z.number().optional(),
  percentage: z.number().min(0).max(100).optional(),
  dueDate: z.string().optional(),
  trigger: z.string().optional(),
})

// Schema pour le contrat cadre
const masterServiceAgreementSchema = z
  .object({
    scope: z.string().optional(),
    sla: z.string().optional(),
    acceptance: z.string().optional(),
    ipOwnership: z.string().optional(),
    confidentiality: z.string().optional(),
    liabilityCap: z.string().optional(),
    termination: z.string().optional(),
    subcontractingAllowed: z.boolean().optional(),
  })
  .optional()

// Schema principal
export const legalEntitySchema = z.object({
  // Étape 1: Informations générales
  name: z.string().min(1, 'Nom interne requis'),
  legalName: z.string().optional(),
  legalForm: z.string().optional(),
  capital: z.string().optional(),
  registrationNumber: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\d{9}$|^\d{14}$/.test(val),
      'SIREN (9 chiffres) ou SIRET (14 chiffres) invalide'
    ),
  vatNumber: z.string().optional(),

  // Étape 2: Contact et représentants
  address: addressSchema,
  contactEmail: z.string().email('Email invalide').optional().or(z.literal('')),
  contactPhone: z.string().optional(),
  website: z.string().url('URL invalide').optional().or(z.literal('')),
  directorName: z.string().optional(),
  publicationDirector: z.string().optional(),

  // Étape 3: Hébergeur
  hostingProvider: hostingProviderSchema,

  // Étape 4: RGPD
  dataControllerName: z.string().optional(),
  dpoEmail: z.string().email('Email invalide').optional().or(z.literal('')),
  privacyContactEmail: z.string().email('Email invalide').optional().or(z.literal('')),
  processingPurposes: z.array(z.string()).optional(),
  legalBases: z.array(z.string()).optional(),
  retentionPolicy: z.string().optional(),
  subProcessors: z.array(z.string()).optional(),
  transferOutsideEu: z.boolean().optional(),
  securityMeasures: z.string().optional(),

  // Étape 5: CGV/CGU
  businessModel: z.string().optional(),
  serviceDescription: z.string().optional(),
  paymentMethods: z.array(z.string()).optional(),
  latePaymentPenalties: z.string().optional(),
  withdrawalPolicy: z.string().optional(),
  refundPolicy: z.string().optional(),
  mediationInfo: z.string().optional(),
  jurisdiction: z.string().optional(),

  // Étape 6: Logiciels et sous-traitance
  softwareUsed: z.array(softwareItemSchema).optional(),
  isSubcontractor: z.boolean().optional(),
  masterServiceAgreement: masterServiceAgreementSchema,
  paymentSchedule: z.array(paymentScheduleItemSchema).optional(),
})

export type LegalEntityFormData = z.infer<typeof legalEntitySchema>
